import React, { useState } from 'react';
import {
  MdClose,
  MdLocationOn,
  MdAttachMoney,
  MdSchedule,
  MdPerson,
  MdEngineering,
  MdBusiness,
  MdCalendarToday,
  MdWarningAmber,
  MdArchive,
  MdInventory,
  MdPictureAsPdf,
  MdImage,
  MdInfoOutline,
} from 'react-icons/md';

// السيرفر اللي الصور والملفات متخزنة عليه
const FILES_BASE_URL = 'https://asfconsult-002-site1.ftempurl.com';

// بناء رابط كامل للصورة/الملف مهما كان شكل المسار الجاي من الـ API
const buildFileUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${FILES_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

// التأكد هل الملف صورة ولا لأ (زي الـ PDF اللي جالنا في الداتا)
const isImageFile = (url) => {
  if (!url) return false;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url);
};

const DEFAULT_AVATAR =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" rx="20" fill="%23E2E8F0"/><circle cx="20" cy="15" r="7" fill="%2394A3B8"/><path d="M6 36c0-8 6-13 14-13s14 5 14 13" fill="%2394A3B8"/></svg>`
  );

// عنصر معلومة موحّد الشكل (أيقونة + تسمية + قيمة)
const InfoField = ({ icon: Icon, label, value, highlight }) => (
  <div className="flex items-start gap-3 py-2.5">
    <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-mainColor/10 text-mainColor">
      <Icon size={17} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-medium text-gray-400">{label}</p>
      <p
        className={`mt-0.5 truncate text-sm font-semibold ${
          highlight ? 'text-mainColor' : 'text-gray-800'
        }`}
        title={typeof value === 'string' ? value : undefined}
      >
        {value ?? '—'}
      </p>
    </div>
  </div>
);

// معرض صور قسم واحد (مع دعم عرض الـ PDF كملف بدل صورة)
const PhotoGallery = ({ title, photos, onPreview }) => {
  if (!photos || photos.length === 0) return null;

  return (
    <div>
      <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
        <MdImage className="text-mainColor" size={18} />
        {title}
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
          {photos.length}
        </span>
      </h4>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
        {photos.map((photo, index) => {
          const url = buildFileUrl(photo?.url || photo);
          const isImg = isImageFile(url);

          if (!isImg) {
            return (
              <a
                key={photo?.id ?? index}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-2 text-gray-500 transition hover:border-mainColor hover:text-mainColor"
                title="فتح الملف"
              >
                <MdPictureAsPdf size={26} />
                <span className="text-[10px] font-medium">فتح الملف</span>
              </a>
            );
          }

          return (
            <button
              type="button"
              key={photo?.id ?? index}
              onClick={() => onPreview(url)}
              className="group aspect-square overflow-hidden rounded-lg border border-gray-200 bg-gray-50"
            >
              <img
                src={url}
                alt={`${title} ${index + 1}`}
                loading="lazy"
                className="h-full w-full object-cover transition duration-200 group-hover:scale-105"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = DEFAULT_AVATAR;
                }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ProjectDetailsModal = ({ project, onClose }) => {
  const [previewUrl, setPreviewUrl] = useState(null);

  if (!project) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (value) => {
    const num = Number(value);
    if (!value || Number.isNaN(num)) return '—';
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      maximumFractionDigits: 0,
    }).format(num);
  };

  const userImageUrl = buildFileUrl(project.userImage);

  const hasAnyPhotos =
    (project.modelPhotos?.length ?? 0) > 0 ||
    (project.sitePhotos?.length ?? 0) > 0 ||
    (project.safetyWastePhotos?.length ?? 0) > 0;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      dir="rtl"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 bg-gradient-to-l from-mainColor to-hoverColor px-6 py-4 text-white">
          <div className="flex items-center gap-3">
            <img
              src={userImageUrl || DEFAULT_AVATAR}
              alt={project.userName || 'user'}
              className="h-11 w-11 rounded-full border-2 border-white/60 object-cover"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_AVATAR;
              }}
            />
            <div>
              <h2 className="text-lg font-bold leading-tight">
                {project.projectName || 'تفاصيل المشروع'}
              </h2>
              <p className="text-xs text-white/80">
                {project.type} {project.userName ? `· ${project.userName}` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-white/90 transition hover:bg-white/20"
            aria-label="إغلاق"
          >
            <MdClose size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {/* Highlight strip */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl bg-blue-50 p-3 text-center">
              <p className="text-[11px] text-gray-500">القيمة</p>
              <p className="mt-1 text-sm font-bold text-blue-700">
                {formatCurrency(project.projectValue)}
              </p>
            </div>
            <div className="rounded-xl bg-purple-50 p-3 text-center">
              <p className="text-[11px] text-gray-500">مدة المشروع</p>
              <p className="mt-1 text-sm font-bold text-purple-700">
                {project.timeOfProject ?? '—'} يوم
              </p>
            </div>
            <div className="rounded-xl bg-amber-50 p-3 text-center">
              <p className="text-[11px] text-gray-500">مدة التنفيذ</p>
              <p className="mt-1 text-sm font-bold text-amber-700">
                {project.durationOfImplementation ?? '—'} يوم
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 p-3 text-center">
              <p className="text-[11px] text-gray-500">حالة الأرشيف</p>
              <p
                className={`mt-1 text-sm font-bold ${
                  project.isArchived ? 'text-gray-500' : 'text-green-600'
                }`}
              >
                {project.isArchived ? 'مؤرشف' : 'غير مؤرشف'}
              </p>
            </div>
          </div>

          {/* Info grid */}
          <div className="grid grid-cols-1 gap-x-6 divide-y divide-gray-100 rounded-xl border border-gray-100 px-4 sm:grid-cols-2 sm:divide-y-0">
            <InfoField icon={MdEngineering} label="نوع المشروع" value={project.type} />
            <InfoField icon={MdLocationOn} label="الموقع" value={project.projectPlace} />
            <InfoField icon={MdLocationOn} label="الحي" value={project.district || 'غير محدد'} />
            <InfoField icon={MdBusiness} label="رقم المحطة" value={project.stationNumber} />
            <InfoField icon={MdPerson} label="العميل" value={project.customer} />
            <InfoField icon={MdPerson} label="المستشار" value={project.consultant} />
            <InfoField icon={MdBusiness} label="الفرع" value={project.branchName} />
            <InfoField icon={MdCalendarToday} label="تاريخ الطلب" value={formatDate(project.orderDate)} />
          </div>

          {/* Notes & safety */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-gray-100 p-4">
              <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-gray-500">
                <MdInfoOutline size={15} /> ملاحظات
              </p>
              <p className="text-sm text-gray-800">{project.note || 'لا توجد ملاحظات'}</p>
            </div>
            <div
              className={`rounded-xl border p-4 ${
                project.safetyViolationsExist
                  ? 'border-red-100 bg-red-50'
                  : 'border-green-100 bg-green-50'
              }`}
            >
              <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-gray-500">
                <MdWarningAmber size={15} /> حالة السلامة
              </p>
              <p
                className={`text-sm font-semibold ${
                  project.safetyViolationsExist ? 'text-red-700' : 'text-green-700'
                }`}
              >
                {project.safetyViolationsExist ? 'يوجد مخالفات' : 'لا يوجد مخالفات'}
              </p>
            </div>
          </div>

          {/* Photos */}
          {hasAnyPhotos && (
            <div className="space-y-4">
              <h3 className="flex items-center gap-2 border-b border-gray-100 pb-2 text-base font-semibold text-gray-800">
                <MdInventory className="text-mainColor" />
                المرفقات والصور
              </h3>
              <PhotoGallery
                title="صور النموذج"
                photos={project.modelPhotos}
                onPreview={setPreviewUrl}
              />
              <PhotoGallery
                title="صور الموقع"
                photos={project.sitePhotos}
                onPreview={setPreviewUrl}
              />
              <PhotoGallery
                title="صور المخلفات"
                photos={project.safetyWastePhotos}
                onPreview={setPreviewUrl}
              />
            </div>
          )}
        </div>
      </div>

      {/* Lightbox لمعاينة الصورة بحجم أكبر */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-6"
          onClick={() => setPreviewUrl(null)}
        >
          <button
            className="absolute top-5 left-5 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            onClick={() => setPreviewUrl(null)}
            aria-label="إغلاق المعاينة"
          >
            <MdClose size={24} />
          </button>
          <img
            src={previewUrl}
            alt="معاينة"
            className="max-h-full max-w-full rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ProjectDetailsModal;